import { useState } from "react";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { MxTabBar } from "@/components/shared/MxTabBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { MapPin, ChevronRight, Plus, ArrowLeft, Pencil, Trash2 } from "lucide-react";
import {
  useInventoryLocations,
  useTopLevelLocations,
  useSubLocations,
  useCreateInventoryLocation,
  useUpdateInventoryLocation,
  useDeleteInventoryLocation,
  type InventoryLocation,
} from "@/hooks/useInventoryLocations";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

// ── Location form dialog ──────────────────────────────────────────────────

function LocationDialog({
  open,
  onOpenChange,
  parentId,
  editing,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  parentId?: string | null;
  editing?: InventoryLocation | null;
}) {
  const create = useCreateInventoryLocation();
  const update = useUpdateInventoryLocation();
  const isEdit = !!editing;

  const [name, setName] = useState(editing?.name ?? "");
  const [address, setAddress] = useState(editing?.address ?? "");
  const [notes, setNotes] = useState(editing?.notes ?? "");

  // Reset when dialog opens
  const handleOpenChange = (v: boolean) => {
    if (v) {
      setName(editing?.name ?? "");
      setAddress(editing?.address ?? "");
      setNotes(editing?.notes ?? "");
    }
    onOpenChange(v);
  };

  const handleSave = async () => {
    if (!name.trim()) return;
    try {
      if (isEdit) {
        await update.mutateAsync({ id: editing!.id, name: name.trim(), address: address.trim() || null, notes: notes.trim() || null });
        toast.success("Location updated");
      } else {
        await create.mutateAsync({ name: name.trim(), parent_id: parentId ?? null, address: address.trim() || null, notes: notes.trim() || null });
        toast.success(parentId ? "Sub-location created" : "Location created");
      }
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e.message ?? "Failed to save");
    }
  };

  const busy = create.isPending || update.isPending;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Edit Location" : parentId ? "New Sub-Location" : "New Location"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-1">
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Barrel Room"
              autoFocus
              onKeyDown={(e) => { if (e.key === "Enter") handleSave(); }}
            />
          </div>
          {!parentId && (
            <div className="space-y-1.5">
              <Label>Address <span className="text-muted-foreground text-xs">(optional)</span></Label>
              <Input
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="123 Main St, Santa Rosa, CA"
              />
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Notes <span className="text-muted-foreground text-xs">(optional)</span></Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Any notes about this location…"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={busy || !name.trim()}>
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Sub-location list (right panel) ──────────────────────────────────────

function LocationDetail({
  location,
  onBack,
  onDeleted,
}: {
  location: InventoryLocation;
  onBack: () => void;
  onDeleted: () => void;
}) {
  const subLocations = useSubLocations(location.id);
  const deleteLocation = useDeleteInventoryLocation();

  const [editOpen, setEditOpen] = useState(false);
  const [addSubOpen, setAddSubOpen] = useState(false);
  const [editingSub, setEditingSub] = useState<InventoryLocation | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleDelete = async (id: string) => {
    try {
      await deleteLocation.mutateAsync(id);
      toast.success("Location deleted");
      if (id === location.id) onDeleted();
    } catch (e: any) {
      toast.error(e.message ?? "Failed to delete");
    }
    setDeletingId(null);
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-3 p-4 border-b">
        <button
          onClick={onBack}
          className="text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="flex-1 min-w-0">
          <h2 className="font-semibold text-foreground truncate">{location.name}</h2>
          {location.address && (
            <p className="text-xs text-muted-foreground truncate">{location.address}</p>
          )}
        </div>
        <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={() => setEditOpen(true)}>
          <Pencil className="h-3.5 w-3.5 mr-1" />
          Edit
        </Button>
        <Button
          variant="ghost" size="sm"
          className="text-muted-foreground hover:text-destructive"
          onClick={() => setDeletingId(location.id)}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div>
          <p className="text-sm font-medium text-muted-foreground mb-3">
            Sub-Locations ({subLocations.length})
          </p>

          {subLocations.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              No sub-locations yet
            </p>
          ) : (
            <div className="space-y-1">
              {subLocations.map((sub) => (
                <div
                  key={sub.id}
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors group"
                >
                  <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-4 w-4 text-primary" />
                  </div>
                  <span className="text-sm font-medium flex-1">{sub.name}</span>
                  {sub.notes && <span className="text-xs text-muted-foreground">{sub.notes}</span>}
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
                    <Button
                      variant="ghost" size="sm"
                      className="h-7 text-xs text-muted-foreground"
                      onClick={() => setEditingSub(sub)}
                    >
                      <Pencil className="h-3 w-3 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="ghost" size="sm"
                      className="h-7 text-muted-foreground hover:text-destructive"
                      onClick={() => setDeletingId(sub.id)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <Button
            variant="outline" size="sm"
            className="mt-3 w-full gap-1.5 text-primary border-dashed"
            onClick={() => setAddSubOpen(true)}
          >
            <Plus className="h-3.5 w-3.5" />
            Add Sub-Location
          </Button>
        </div>
      </div>

      {/* Dialogs */}
      <LocationDialog open={editOpen} onOpenChange={setEditOpen} editing={location} />
      <LocationDialog open={addSubOpen} onOpenChange={setAddSubOpen} parentId={location.id} />
      <LocationDialog
        open={!!editingSub}
        onOpenChange={(v) => { if (!v) setEditingSub(null); }}
        editing={editingSub}
        parentId={location.id}
      />

      <AlertDialog open={!!deletingId} onOpenChange={(v) => { if (!v) setDeletingId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete location?</AlertDialogTitle>
            <AlertDialogDescription>
              This will also delete all sub-locations. Any parts assigned here will lose their location data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deletingId && handleDelete(deletingId)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────

export default function Locations() {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [newLocationOpen, setNewLocationOpen] = useState(false);

  const topLevel = useTopLevelLocations();
  const { data: allLocations } = useInventoryLocations();

  const selected = allLocations?.find((l) => l.id === selectedId) ?? null;

  const subCount = (locationId: string) =>
    allLocations?.filter((l) => l.parent_id === locationId).length ?? 0;

  return (
    <div className="flex flex-col h-screen">
      <header className="sticky top-0 z-20 flex h-16 items-center gap-4 border-b bg-card px-6">
        <SidebarTrigger />
        <div className="flex-1">
          <h1 className="text-lg font-semibold text-foreground">Locations</h1>
        </div>
        <Button size="sm" className="gap-1.5" onClick={() => setNewLocationOpen(true)}>
          <Plus className="h-4 w-4" />
          New Location
        </Button>
      </header>
      <MxTabBar />

      <div className="flex flex-1 min-h-0">
        {/* Left panel — location list */}
        <div className="w-96 border-r flex flex-col min-h-0 bg-card">
          <div className="flex-1 overflow-y-auto">
            {topLevel.map((location) => {
              const count = subCount(location.id);
              const isSelected = selectedId === location.id;

              return (
                <button
                  key={location.id}
                  onClick={() => setSelectedId(isSelected ? null : location.id)}
                  className={cn(
                    "w-full text-left flex items-center gap-3 px-4 py-3 border-b transition-colors hover:bg-muted/50",
                    isSelected && "border-l-[3px] border-l-primary bg-muted/40"
                  )}
                >
                  <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{location.name}</p>
                    {location.address && (
                      <p className="text-xs text-muted-foreground truncate">{location.address}</p>
                    )}
                    {count > 0 && (
                      <span className="inline-flex items-center gap-1 mt-0.5 text-xs text-primary font-medium">
                        {count} Sub-Location{count !== 1 ? "s" : ""}
                        <ChevronRight className="h-3 w-3" />
                      </span>
                    )}
                  </div>
                </button>
              );
            })}

            {topLevel.length === 0 && (
              <div className="p-8 text-center text-sm text-muted-foreground">
                No locations yet.{" "}
                <button
                  className="text-primary underline underline-offset-2"
                  onClick={() => setNewLocationOpen(true)}
                >
                  Create one to get started.
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Right panel — detail */}
        <div className="flex-1 min-h-0 bg-background">
          {selected ? (
            <LocationDetail
              location={selected}
              onBack={() => setSelectedId(null)}
              onDeleted={() => setSelectedId(null)}
            />
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground gap-2">
              <MapPin className="h-8 w-8 opacity-30" />
              <p className="text-sm">Select a location to view sub-locations</p>
            </div>
          )}
        </div>
      </div>

      <LocationDialog open={newLocationOpen} onOpenChange={setNewLocationOpen} />
    </div>
  );
}
